import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  IndianRupee,
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  AlertTriangle,
  Clock,
  Calendar,
  Activity,
  Target,
  BarChart3,
  Mail,
  User,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { DashboardLayout } from '@/components/DashboardLayout';
import { formatCurrency, cn, formatTimeAgo } from '@/lib/utils';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
} from 'recharts';

// TypeScript Interfaces
interface ExecutivePerformanceData {
  id: string;
  name: string;
  email: string;
  role: string;
  lastActive: string;
  lastLogin: string;
  leads: {
    total: number;
    createdThisMonth: number;
    active: number;
    assigned: number;
    created: number;
  };
  conversions: {
    won: number;
    wonThisMonth: number;
    lost: number;
    lostThisMonth: number;
    conversionRate: number;
    conversionRateLabel?: string;
    isLowSample?: boolean;
  };
  revenue: {
    thisMonth: number;
    total: number;
    pipelineValue: number;
    avgDealSize: number;
  };
  efficiency: {
    avgDaysToClose: number;
    leadsPerDay: number | string;
  };
}

interface ExecutivePerformanceResponse {
  success: boolean;
  data: ExecutivePerformanceData[];
  summary: {
    totalExecutives: number;
    totalLeadsManaged: number;
    totalRevenueThisMonth: number;
    totalPipelineValue: number;
    avgConversionRate: number;
  };
}

// Backend response structure (different from list view)
interface ExecutiveDetailBackendData {
  executive: {
    id: string;
    name: string;
    email: string;
    role: string;
    department?: string;
    position?: string;
    phone?: string;
    lastLogin?: string;
    loginCount?: number;
    manager?: { id: string; name: string; email: string };
  };
  leads: {
    total: number;
    active: number;
    assigned: number;
    created: number;
    createdThisMonth: number;
    stageDistribution: Record<string, number>;
  };
  conversions: {
    won: number;
    wonThisMonth: number;
    lost: number;
    lostThisMonth: number;
    conversionRate: number;
    conversionRateLabel?: string;
    isLowSample?: boolean;
    conversionRateThisMonth: number;
  };
  revenue: {
    thisMonth: number;
    total: number;
    pipelineValue: number;
    avgDealSize: number;
    largestDeal: number;
  };
  activities: {
    thisMonth: number;
    thisWeek: number;
    total: number;
    breakdown: Record<string, number>;
    recentActivity: string | null;
  };
  tasks: {
    total: number;
    pending: number;
    overdue: number;
    completedThisMonth: number;
    upcoming: Array<any>;
  };
  efficiency: {
    avgDaysToClose: number;
    leadsPerDay: string;
    activitiesPerLead: string;
  };
  recentLeads: Array<any>;
  trends?: {
    daily: Array<{ date: string; leadsCreated: number; activities: number; revenue: number }>;
    weekly: Array<{ weekStart: string; dealsWon: number; revenue: number }>;
    monthly: Array<{ month: string; label: string; revenue: number; dealsWon: number }>;
  };
}

interface ExecutiveDetailResponse {
  success: boolean;
  data: ExecutiveDetailBackendData;
  meta?: {
    month: string;
    timestamp: string;
  };
}

// Calculate Health Score
const calculateHealthScore = (executive: ExecutivePerformanceData): number => {
  let score = 0;

  // Conversion rate weight: 30%
  const conversionScore = Math.min(executive.conversions.conversionRate * 3, 30);
  score += conversionScore;

  // Active leads weight: 20%
  const activeLeadsScore = Math.min(executive.leads.active * 2, 20);
  score += activeLeadsScore;

  // Avg close days weight: 20% (lower is better)
  const avgCloseDays = executive.efficiency.avgDaysToClose || 30;
  const closeDaysScore = avgCloseDays <= 7 ? 20 : avgCloseDays <= 14 ? 15 : avgCloseDays <= 30 ? 10 : 5;
  score += closeDaysScore;

  // This month's activity weight: 15%
  const monthlyLeads = executive.leads.createdThisMonth;
  const monthlyWon = executive.conversions.wonThisMonth;
  const activityScore = Math.min((monthlyLeads + monthlyWon * 3) * 1.5, 15);
  score += activityScore;

  // Pipeline value weight: 15%
  const pipelineScore = executive.revenue.pipelineValue > 500000 ? 15 :
                        executive.revenue.pipelineValue > 200000 ? 12 :
                        executive.revenue.pipelineValue > 100000 ? 9 :
                        executive.revenue.pipelineValue > 50000 ? 6 : 3;
  score += pipelineScore;

  return Math.round(Math.min(score, 100));
};

// Get health score color
const getHealthScoreColor = (score: number): string => {
  if (score >= 80) return 'text-green-600 bg-green-100';
  if (score >= 60) return 'text-yellow-600 bg-yellow-100';
  return 'text-red-600 bg-red-100';
};

// Get health score label
const getHealthScoreLabel = (score: number): string => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Needs Improvement';
  return 'Critical';
};

// Summary Card Component
const SummaryCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  isLoading?: boolean;
}> = ({ title, value, icon, color, isLoading }) => (
  <Card className={cn('border', color)}>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-24 mt-1" />
          ) : (
            <p className="text-2xl font-bold text-gray-900">{value}</p>
          )}
        </div>
        <div className={cn('p-3 rounded-lg', color)}>
          {icon}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Performance Table Component
const PerformanceTable: React.FC<{
  data: ExecutivePerformanceData[];
  isLoading?: boolean;
  onViewDetails: (id: string) => void;
}> = ({ data, isLoading, onViewDetails }) => {
  const [sortBy, setSortBy] = useState<string>('healthScore');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortValue = (executive: ExecutivePerformanceData, field: string): number | string => {
    switch (field) {
      case 'name':
        return executive.name.toLowerCase();
      case 'totalLeads':
        return executive.leads.total;
      case 'monthlyLeads':
        return executive.leads.createdThisMonth;
      case 'won':
        return executive.conversions.won;
      case 'lost':
        return executive.conversions.lost;
      case 'conversion':
        return executive.conversions.conversionRate;
      case 'pipelineValue':
        return executive.revenue.pipelineValue;
      case 'monthlyRevenue':
        return executive.revenue.thisMonth;
      case 'avgCloseDays':
        return executive.efficiency.avgDaysToClose;
      case 'healthScore':
        return calculateHealthScore(executive);
      default:
        return 0;
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aValue = getSortValue(a, sortBy);
    const bValue = getSortValue(b, sortBy);

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const SortHeader: React.FC<{ field: string; label: string }> = ({ field, label }) => (
    <th
      className="text-left p-3 cursor-pointer hover:bg-gray-50 whitespace-nowrap"
      onClick={() => handleSort(field)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(field); } }}
      tabIndex={0}
      role="columnheader"
      aria-sort={sortBy === field ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      aria-label={`Sort by ${label}`}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === field && (
          sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
        )}
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div role="status" aria-label="Loading executive performance data" className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
            <span className="sr-only">Loading...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">Executive Performance Table</CardTitle>
        <CardDescription>Click on any executive row to view detailed performance</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Executive performance data">
            <thead>
              <tr className="border-b bg-gray-50">
                <SortHeader field="name" label="Executive" />
                <SortHeader field="totalLeads" label="Leads (Total/Month)" />
                <SortHeader field="won" label="Won/Lost" />
                <SortHeader field="conversion" label="Conversion %" />
                <SortHeader field="pipelineValue" label="Pipeline Value" />
                <SortHeader field="monthlyRevenue" label="Bookings (MTD)" />
                <SortHeader field="avgCloseDays" label="Avg Close Days" />
                <SortHeader field="healthScore" label="Health Score" />
                <th className="text-left p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((executive) => {
                const healthScore = calculateHealthScore(executive);
                const healthColor = getHealthScoreColor(healthScore);

                return (
                  <tr
                    key={executive.id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => onViewDetails(executive.id)}
                    tabIndex={0}
                    role="row"
                    aria-label={`View details for ${executive.name}, ${executive.leads.total} leads, ${Number(executive.conversions.conversionRate || 0).toFixed(1)}% conversion`}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewDetails(executive.id); } }}
                  >
                    <td className="p-3">
                      <div>
                        <p className="font-medium text-gray-900">{executive.name}</p>
                        <p className="text-xs text-gray-500">{executive.email}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="font-medium">{executive.leads.total}</span>
                      <span className="text-gray-500"> / {executive.leads.createdThisMonth}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-green-600 font-medium">{executive.conversions.won}</span>
                      <span className="text-gray-500"> / </span>
                      <span className="text-red-600 font-medium">{executive.conversions.lost}</span>
                    </td>
                    <td className="p-3">
                      <span className={cn(
                        'font-medium',
                        executive.conversions.isLowSample ? 'text-gray-400 italic' :
                        executive.conversions.conversionRate >= 20 ? 'text-green-600' :
                        executive.conversions.conversionRate >= 10 ? 'text-yellow-600' : 'text-red-600'
                      )}>
                        {executive.conversions.conversionRateLabel || `${Number(executive.conversions.conversionRate || 0).toFixed(1)}%`}
                      </span>
                      {executive.conversions.isLowSample && (
                        <span className="ml-1 text-xs text-gray-400" title="Low sample size (fewer than 5 closed deals)">*</span>
                      )}
                    </td>
                    <td className="p-3 font-medium">
                      {formatCurrency(executive.revenue.pipelineValue)}
                    </td>
                    <td className="p-3 font-medium">
                      {formatCurrency(executive.revenue.thisMonth)}
                    </td>
                    <td className="p-3">
                      {executive.efficiency.avgDaysToClose} days
                    </td>
                    <td className="p-3">
                      <span className={cn('px-2 py-1 rounded-full text-sm font-medium', healthColor)}>
                        {healthScore}
                      </span>
                    </td>
                    <td className="p-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`View details for ${executive.name}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewDetails(executive.id);
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {data.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium">No executive data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Executive Detail View Component
const ExecutiveDetailView: React.FC<{
  executiveId: string;
  onBack: () => void;
}> = ({ executiveId, onBack }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: detailData, isLoading, refetch } = useQuery({
    queryKey: ['executive-performance-detail', executiveId],
    queryFn: async (): Promise<ExecutiveDetailResponse> => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/executive-performance/${executiveId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch executive details');
      }

      return response.json();
    },
    refetchInterval: 30000
  });

  // Map backend response to expected frontend structure
  const rawData = detailData?.data;
  const executive = rawData ? {
    id: rawData.executive?.id || executiveId,
    name: rawData.executive?.name || 'Unknown',
    email: rawData.executive?.email || '',
    role: rawData.executive?.role || '',
    lastActive: rawData.activities?.recentActivity || rawData.executive?.lastLogin || '',
    lastLogin: rawData.executive?.lastLogin || '',
    leads: {
      total: rawData.leads?.total || 0,
      createdThisMonth: rawData.leads?.createdThisMonth || 0,
      active: rawData.leads?.active || 0,
      assigned: rawData.leads?.assigned || 0,
      created: rawData.leads?.created || 0,
    },
    conversions: {
      won: rawData.conversions?.won || 0,
      wonThisMonth: rawData.conversions?.wonThisMonth || 0,
      lost: rawData.conversions?.lost || 0,
      lostThisMonth: rawData.conversions?.lostThisMonth || 0,
      conversionRate: rawData.conversions?.conversionRate || 0,
      conversionRateLabel: rawData.conversions?.conversionRateLabel,
      isLowSample: rawData.conversions?.isLowSample,
    },
    revenue: {
      thisMonth: rawData.revenue?.thisMonth || 0,
      total: rawData.revenue?.total || 0,
      pipelineValue: rawData.revenue?.pipelineValue || 0,
      avgDealSize: rawData.revenue?.avgDealSize || 0,
    },
    efficiency: {
      avgDaysToClose: rawData.efficiency?.avgDaysToClose || 0,
      leadsPerDay: Number(rawData.efficiency?.leadsPerDay) || 0,
    }
  } : null;

  const recentActivities = rawData?.activities?.breakdown ?
    Object.entries(rawData.activities.breakdown).map(([type, count], index) => ({
      id: `activity-${index}`,
      type,
      description: `${count} ${type.toLowerCase()} activities`,
      createdAt: rawData.activities.recentActivity || new Date().toISOString(),
      leadName: undefined as string | undefined,
    })) : [];

  if (isLoading) {
    return (
      <div className="space-y-6" role="status" aria-label="Loading executive details">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  if (!executive) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <h3 className="text-lg font-medium text-gray-900">Executive not found</h3>
        <p className="text-gray-500 mt-2">Unable to load executive performance data</p>
        <Button variant="outline" onClick={onBack} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
      </div>
    );
  }

  const healthScore = calculateHealthScore(executive);
  const healthColor = getHealthScoreColor(healthScore);
  const healthLabel = getHealthScoreLabel(healthScore);

  // Calculate risk factors
  const riskFactors: Array<{ label: string; severity: 'high' | 'medium' | 'low' }> = [];

  if (executive.conversions.conversionRate < 10) {
    riskFactors.push({ label: 'Low conversion rate (< 10%)', severity: 'high' });
  }

  if (executive.efficiency.avgDaysToClose > 30) {
    riskFactors.push({ label: 'High average close time (> 30 days)', severity: 'medium' });
  }

  if (executive.leads.createdThisMonth === 0) {
    riskFactors.push({ label: 'No leads created this month', severity: 'high' });
  }

  if (executive.revenue.pipelineValue < 50000) {
    riskFactors.push({ label: 'Low pipeline value (< 50,000)', severity: 'medium' });
  }

  const lastActiveDate = new Date(executive.lastActive);
  const daysSinceActive = Math.floor((Date.now() - lastActiveDate.getTime()) / (1000 * 60 * 60 * 24));
  if (daysSinceActive >= 3) {
    riskFactors.push({ label: `No activity in ${daysSinceActive} days`, severity: 'high' });
  }

  // Calculate estimated work hours (based on activities)
  const estimatedWeeklyHours = Math.min(
    (executive.leads.createdThisMonth * 2 + executive.conversions.wonThisMonth * 3) * 0.5,
    60
  );
  const workHoursTarget = 60;
  const workHoursPercentage = Math.round((estimatedWeeklyHours / workHoursTarget) * 100);

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to List
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetch();
            toast({
              title: "Data refreshed",
              description: "Executive performance data has been updated"
            });
          }}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Executive Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-2xl font-bold">
                  {executive.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{executive.name}</h2>
                <p className="text-gray-500">{executive.role}</p>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {executive.email}
                </p>
              </div>
            </div>
            <div className="text-center">
              <div className={cn('inline-flex items-center justify-center w-20 h-20 rounded-full text-2xl font-bold', healthColor)}>
                {healthScore}
              </div>
              <p className="text-sm font-medium text-gray-600 mt-2">Health Score</p>
              <p className={cn('text-xs', healthColor.split(' ')[0])}>{healthLabel}</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Last Active: {formatTimeAgo(executive.lastActive)}
            </span>
            <span className="flex items-center gap-1">
              <User className="h-4 w-4" />
              Last Login: {formatTimeAgo(executive.lastLogin)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/pipeline?assignedTo=${executiveId}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/pipeline?assignedTo=${executiveId}`); } }}
          aria-label={`View ${executive.leads.total} leads managed by ${executive.name} in pipeline`}
        >
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Total Leads Managed</p>
            <p className="text-2xl font-bold">{executive.leads.total}</p>
            <p className="text-xs text-blue-500 mt-1">View in pipeline</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Leads This Month</p>
            <p className="text-2xl font-bold">{executive.leads.createdThisMonth}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Conversion Rate{executive.conversions.isLowSample ? ' *' : ''}</p>
            <p className={cn('text-2xl font-bold', executive.conversions.isLowSample && 'text-gray-400')}>
              {executive.conversions.conversionRateLabel || `${Number(executive.conversions.conversionRate || 0).toFixed(1)}%`}
            </p>
            {executive.conversions.isLowSample && (
              <p className="text-xs text-gray-400 mt-1">* Low sample (&lt;5 closed deals)</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500" title="Lifetime sum of dealValue across all WON deals (bookings, not collected cash).">Lifetime Bookings</p>
            <p className="text-2xl font-bold">{formatCurrency(executive.revenue.total)}</p>
          </CardContent>
        </Card>
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(`/pipeline?assignedTo=${executiveId}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/pipeline?assignedTo=${executiveId}`); } }}
          aria-label={`View pipeline worth ${formatCurrency(executive.revenue.pipelineValue)} for ${executive.name}`}
        >
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Pipeline Value</p>
            <p className="text-2xl font-bold">{formatCurrency(executive.revenue.pipelineValue)}</p>
            <p className="text-xs text-blue-500 mt-1">View in pipeline</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Avg Deal Size</p>
            <p className="text-2xl font-bold">{formatCurrency(executive.revenue.avgDealSize)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Avg Close Days</p>
            <p className="text-2xl font-bold">{executive.efficiency.avgDaysToClose} days</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Leads Per Day</p>
            <p className="text-2xl font-bold">{Number(executive.efficiency.leadsPerDay || 0).toFixed(1)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Stats & Work Hours */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversion Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5" />
              Conversion Statistics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Won (Total)
                </span>
                <span className="font-bold text-green-600">{executive.conversions.won}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Won This Month
                </span>
                <span className="font-bold text-green-500">{executive.conversions.wonThisMonth}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  Lost (Total)
                </span>
                <span className="font-bold text-red-600">{executive.conversions.lost}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  Lost This Month
                </span>
                <span className="font-bold text-red-500">{executive.conversions.lostThisMonth}</span>
              </div>
              <div
                className="pt-2 border-t flex items-center justify-between"
                title="Sum of dealValue for leads moved to WON this month. Bookings, not collected cash (see Finance dashboard for collections)."
              >
                <span className="font-medium">Bookings (MTD)</span>
                <span className="font-bold text-blue-600">
                  {formatCurrency(executive.revenue.thisMonth)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Work Hours Tracking */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Work Hours Tracking
            </CardTitle>
            <CardDescription>
              Target: 10 hours/day, 6 days/week = 60 hours/week
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Estimated Hours This Week</span>
                  <span className="font-bold">{estimatedWeeklyHours.toFixed(1)} / {workHoursTarget}h</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={cn(
                      'h-3 rounded-full transition-all',
                      workHoursPercentage >= 80 ? 'bg-green-500' :
                      workHoursPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    )}
                    style={{ width: `${Math.min(workHoursPercentage, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {workHoursPercentage}% of target
                </p>
              </div>
              <p className="text-xs text-gray-400 italic">
                * Estimated based on logged activities (leads created, deals closed)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Risk Factors Panel */}
      {riskFactors.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Risk Factors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {riskFactors.map((risk, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex items-center gap-2 p-2 rounded',
                    risk.severity === 'high' ? 'bg-red-100 text-red-800' :
                    risk.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  )}
                >
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">{risk.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Period Comparison Placeholder */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Performance Trends
          </CardTitle>
          <CardDescription>
            Daily, weekly, and monthly performance comparison
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Last 7 Days — Leads Created</h4>
              <div className="h-32">
                {rawData?.trends?.daily && rawData.trends.daily.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rawData.trends.daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: string) => v.slice(5)}
                      />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={28} />
                      <RechartsTooltip />
                      <Line
                        type="monotone"
                        dataKey="leadsCreated"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Leads"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    No data
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Last 4 Weeks — Deals Won</h4>
              <div className="h-32">
                {rawData?.trends?.weekly && rawData.trends.weekly.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rawData.trends.weekly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis
                        dataKey="weekStart"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(v: string) => v.slice(5)}
                      />
                      <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={28} />
                      <RechartsTooltip />
                      <Bar dataKey="dealsWon" fill="#16a34a" name="Deals Won" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    No data
                  </div>
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Last 3 Months — Bookings</h4>
              <div className="h-32">
                {rawData?.trends?.monthly && rawData.trends.monthly.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rawData.trends.monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        width={40}
                        tickFormatter={(v: number) =>
                          v >= 10000000 ? `${(v / 10000000).toFixed(1)}Cr` :
                          v >= 100000 ? `${(v / 100000).toFixed(1)}L` :
                          v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`
                        }
                      />
                      <RechartsTooltip
                        formatter={(v: number) => formatCurrency(v)}
                      />
                      <Bar dataKey="revenue" fill="#7c3aed" name="Bookings" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    No data
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Activity Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivities.length > 0 ? (
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <div key={activity.id || index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Activity className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.type}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                    {activity.leadName && (
                      <p className="text-xs text-gray-500 mt-1">Lead: {activity.leadName}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatTimeAgo(activity.createdAt)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm font-medium">No recent activities</p>
              <p className="text-xs mt-1">Activity will appear here as the executive takes actions</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Executive Performance Page Component
export const ExecutivePerformancePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: executiveId } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [timePeriod, setTimePeriod] = useState('30d');

  // Fetch executive performance data - MUST be before any early returns
  const { data: performanceData, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['executive-performance', timePeriod],
    queryFn: async (): Promise<ExecutivePerformanceResponse> => {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/executive-performance?period=${timePeriod}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch executive performance data');
      }

      return response.json();
    },
    refetchInterval: 30000
  });

  const executives = performanceData?.data || [];
  const summary = performanceData?.summary;

  // Redirect if not CEO - AFTER all hooks
  if (user?.role !== 'CEO') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-12">
          <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-2">This page is only accessible to CEO users.</p>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="mt-4"
          >
            Return to Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // If viewing detail view
  if (executiveId) {
    return (
      <DashboardLayout>
        <ExecutiveDetailView
          executiveId={executiveId}
          onBack={() => navigate('/executive-performance')}
        />
      </DashboardLayout>
    );
  }

  const handleRefresh = () => {
    refetch();
    toast({
      title: "Dashboard refreshed",
      description: "Latest performance data has been loaded"
    });
  };

  const handleExport = () => {
    // Generate CSV data
    const headers = ['Executive', 'Email', 'Total Leads', 'This Month', 'Won', 'Lost', 'Conversion %', 'Pipeline Value', 'Bookings (MTD)', 'Avg Close Days', 'Health Score'];
    const rows = executives.map(exec => [
      exec.name,
      exec.email,
      exec.leads.total,
      exec.leads.createdThisMonth,
      exec.conversions.won,
      exec.conversions.lost,
      Number(exec.conversions.conversionRate || 0).toFixed(1),
      exec.revenue.pipelineValue,
      exec.revenue.thisMonth,
      exec.efficiency.avgDaysToClose,
      calculateHealthScore(exec)
    ]);

    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `executive-performance-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export successful",
      description: "Performance data has been exported to CSV"
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <nav aria-label="Breadcrumb" className="text-sm text-gray-500 mb-1">
              <button onClick={() => navigate('/sales-dashboard')} className="hover:text-blue-600 hover:underline">Sales Dashboard</button>
              <span className="mx-1">/</span>
              <span className="text-gray-900 font-medium">Executive Performance</span>
            </nav>
            <h1 className="text-3xl font-bold text-gray-900">Executive Performance Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Monitor and analyze team performance metrics
              {isFetching && performanceData && (
                <span className="text-xs text-gray-400 animate-pulse ml-2">Updating...</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={timePeriod} onValueChange={setTimePeriod}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Time Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">This Week</SelectItem>
                <SelectItem value="30d">This Month</SelectItem>
                <SelectItem value="last30d">Last 30 Days</SelectItem>
                <SelectItem value="quarter">This Quarter</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              aria-label="Refresh dashboard data"
            >
              <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Executives"
            value={summary?.totalExecutives || executives.length}
            icon={<Users className="h-5 w-5 text-blue-600" />}
            color="bg-blue-50 border-blue-200"
            isLoading={isLoading}
          />
          <SummaryCard
            title="Total Pipeline Value"
            value={formatCurrency(summary?.totalPipelineValue || 0)}
            icon={<IndianRupee className="h-5 w-5 text-green-600" />}
            color="bg-green-50 border-green-200"
            isLoading={isLoading}
          />
          <SummaryCard
            title="Avg Conversion Rate"
            value={`${Number(summary?.avgConversionRate || 0).toFixed(1)}%`}
            icon={<TrendingUp className="h-5 w-5 text-purple-600" />}
            color="bg-purple-50 border-purple-200"
            isLoading={isLoading}
          />
          <SummaryCard
            title="Bookings (MTD)"
            value={formatCurrency(summary?.totalRevenueThisMonth || 0)}
            icon={<Target className="h-5 w-5 text-orange-600" />}
            color="bg-orange-50 border-orange-200"
            isLoading={isLoading}
          />
        </div>

        {/* Error State */}
        {error && (
          <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-800">Failed to load performance data</p>
                <p className="text-sm text-red-600">{error instanceof Error ? error.message : 'An unexpected error occurred'}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="text-red-700 border-red-300 hover:bg-red-100" aria-label="Retry loading performance data">
              <RefreshCw className="h-4 w-4 mr-1" /> Retry
            </Button>
          </div>
        )}

        {/* Performance Table */}
        <PerformanceTable
          data={executives}
          isLoading={isLoading}
          onViewDetails={(id) => navigate(`/executive-performance/${id}`)}
        />
      </div>
    </DashboardLayout>
  );
};
